import { useEffect, useRef, useState } from 'react';
import { Client, type IMessage } from '@stomp/stompjs';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Otevre STOMP WebSocket spojeni a invaliduje React Query cache pri prijeti zprav
 * pro danou competition (shortCode).
 */
export const useCompetitionLive = (shortCode: string | undefined) => {
  const queryClient = useQueryClient();
  const clientRef = useRef<Client | null>(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!shortCode) return;

    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const host = window.location.host;
    const wsUrl = `${protocol}://${host}/ws`;

    const client = new Client({
      brokerURL: wsUrl,
      reconnectDelay: 2000,
      heartbeatIncoming: 10_000,
      heartbeatOutgoing: 10_000,
      onConnect: () => {
        setConnected(true);
        // Po (re)connectu dohnat stav — pri vypadku WS jsou broadcasty ztracene,
        // tak vynutime refetch pro vsechny queries souteze.
        queryClient.invalidateQueries({ queryKey: ['public', shortCode] });
        client.subscribe(`/topic/competition/${shortCode}`, (msg: IMessage) => {
          try {
            JSON.parse(msg.body);  // parsujeme jen pro validaci
          } catch {
            // ignoruj
          }
          // Invaliduj queries souvisejici se soutezi
          queryClient.invalidateQueries({ queryKey: ['public', shortCode] });
        });
      },
      onDisconnect: () => setConnected(false),
      onStompError: () => setConnected(false),
      onWebSocketError: () => setConnected(false),
    });

    client.activate();
    clientRef.current = client;

    return () => {
      client.deactivate();
      setConnected(false);
    };
  }, [shortCode, queryClient]);

  return { connected };
};
